import { useEffect, useCallback } from "react";
import useForgeRock from "./useForgeRock";
import { checkFeatureFlag as buildCheckFeatureFlag } from "../services/apis";
import { CheckFeatureFlagApiResponse } from "../types/CheckFeatureFlagApiResponse";
import { useAppDispatch, useAppSelector } from "./reduxhooks";
import { setFeatureFlag } from "../redux/slices/featureFlagsSlice";
import { FeatureFlagEntry } from "../types/featureFlags";

export default function useCheckFeatureFlag(featureName: string) {
    const { APICall } = useForgeRock();
    const dispatch = useAppDispatch();
    const trimmedName = featureName.trim();
    const cached: FeatureFlagEntry | undefined = useAppSelector(
        (state: { featureFlagsSlice: { flags: Record<string, FeatureFlagEntry> } }) => state.featureFlagsSlice?.flags?.[trimmedName]
    );

    const isAuthed = useAppSelector((state) => state.userSlice.isAuthenticated);

    const ensureFetched = useCallback((): Promise<FeatureFlagEntry> => {
        if (trimmedName && cached?.ready) return Promise.resolve(cached);
        if (!trimmedName) {
            const entry: FeatureFlagEntry = { enabled: false, ready: true };
            return Promise.resolve(entry);
        }
        return new Promise((resolve) => {
            let settled = false;
            APICall(
                buildCheckFeatureFlag({ FeatureName: trimmedName })
            )
                .then((data: CheckFeatureFlagApiResponse) => {
                    if (settled) return;
                    const enabled = data.resultCode === 0 && data.response;
                    const entry: FeatureFlagEntry = { enabled, ready: true };
                    dispatch(setFeatureFlag({ name: trimmedName, enabled, ready: true }));
                    settled = true;
                    resolve(entry);
                })
                .catch(() => {
                    if (settled) return;
                    const entry: FeatureFlagEntry = { enabled: false, ready: true };
                    dispatch(setFeatureFlag({ name: trimmedName, enabled: false, ready: true }));
                    settled = true;
                    resolve(entry);
                });
        });
    }, [trimmedName, cached, APICall, dispatch]);

    useEffect(() => {
        // Do not mutate store for empty / whitespace names; treat as a no-op
        if (!isAuthed) return;
        if (!trimmedName) return;
        if (cached?.ready) return; // Already fetched

        APICall(
          buildCheckFeatureFlag({
            FeatureName: trimmedName,
          })
        )
          .then((data: CheckFeatureFlagApiResponse) => {
            const enabled = data.resultCode === 0 && data.response;
            dispatch(
              setFeatureFlag({ name: trimmedName, enabled, ready: true })
            );
          })
          .catch(() => {
            const entry: FeatureFlagEntry = { enabled: false, ready: true };
            return Promise.resolve(entry);
          });
    }, [trimmedName, APICall, isAuthed]);

    if (!trimmedName) return { enabled: false, ready: true, ensureFetched };
    return { enabled: cached?.enabled ?? false, ready: cached?.ready ?? false, ensureFetched };
}
